import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { postData } from '../../services/api';

export default function RegistrationPathSelector({
  currentCollegeSlug = '',
  tenantSlug = '',
  onTenantResolved,
}) {
  const navigate = useNavigate();
  const [options, setOptions] = useState({ tenant: null, colleges: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const query = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    const requestPath = `/training/college-access/public/registration-options${query}`;
    setLoading(true);
    console.info('[registration-options] request', {
      method: 'POST',
      path: requestPath,
      tenant: tenantSlug || '(resolve from host)',
      hostname: window.location.hostname,
    });
    postData(requestPath)
      .then((response) => {
        if (!active) return;
        const payload = response?.data || response || {};
        const next = {
          tenant: payload.tenant || null,
          colleges: Array.isArray(payload.colleges) ? payload.colleges : [],
        };
        console.info('[registration-options] response', {
          responseType: typeof payload,
          resolvedTenant: next.tenant?.slug || '',
          collegeCount: next.colleges.length,
          colleges: next.colleges.map((college) => college.name),
        });
        if (typeof payload === 'string' && payload.trim().toLowerCase().startsWith('<!doctype html')) {
          console.warn('[registration-options] API returned the frontend HTML instead of registration JSON.');
        }
        setOptions(next);
        setError('');
        if (next.tenant && onTenantResolved) onTenantResolved(next.tenant);
      })
      .catch((requestError) => {
        if (!active) return;
        console.error('[registration-options] failed', {
          status: requestError.response?.status || 0,
          message: requestError.response?.data?.message || requestError.message,
          response: requestError.response?.data || null,
        });
        setOptions({ tenant: null, colleges: [] });
        setError(requestError.response?.data?.message || 'Registration options could not be loaded.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [onTenantResolved, tenantSlug]);

  const selectedValue = useMemo(
    () => String(currentCollegeSlug || '').trim().toLowerCase() || 'individual',
    [currentCollegeSlug]
  );

  const handleChange = (event) => {
    const value = event.target.value;
    const resolvedTenantSlug = options.tenant?.slug || tenantSlug;
    if (value === 'individual') {
      navigate(resolvedTenantSlug
        ? `/register/user?tenant=${encodeURIComponent(resolvedTenantSlug)}&registration=individual`
        : '/register/user');
      return;
    }

    const college = options.colleges.find((item) => String(item.slug) === value);
    if (!college) return;
    if (college.registrationUrl) {
      const destination = new URL(college.registrationUrl, window.location.origin);
      navigate(`${destination.pathname}${destination.search}`);
      return;
    }
    const params = new URLSearchParams();
    if (resolvedTenantSlug) params.set('tenant', resolvedTenantSlug);
    if (college.uniqueAccessCode) params.set('collegeCode', college.uniqueAccessCode);
    navigate(`/${encodeURIComponent(college.slug)}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  return (
    <div className="auth-registration-path mb-3">
      <label className="form-label" htmlFor="registration-path">Registering As *</label>
      <div className="input-group">
        <span className="input-group-text"><i className="bi bi-mortarboard-fill" /></span>
        <select
          id="registration-path"
          className="form-select"
          value={selectedValue}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="individual">Individual</option>
          {options.colleges.map((college) => (
            <option key={college.id || college.slug} value={college.slug}>{college.name}</option>
          ))}
        </select>
      </div>
      {loading && <div className="auth-registration-path-note"><span className="spinner-border spinner-border-sm" /> Loading colleges...</div>}
      {!loading && !error && <div className="auth-registration-path-note">Choose Individual or your MOU-signed college.</div>}
      {error && <div className="auth-registration-path-note is-error">{error}</div>}
    </div>
  );
}
